import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Select } from "risa-oasis-ui_v2";
import CustomTable from "../../../components/custom-table/custom-table";
import { TableCellType } from "../../../components/custom-table/table";
import { ClinicalData } from "../../../data-model/clinicalCondition";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { formatJcodeIcd } from "../../../pages/singleOrderLayout/utils/getJCode";
import { RootState } from "../../../redux/store/store";
import GreenBullet from "../../../svg/green-bullet";
import { usePrimaryIcdCodes } from "../../../utils/getPrimaryDiagnosisCodes";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import InfoBadge from "../../infoBadge/infoBadge";
import "./clinicalDataTable.scss";
import {
  dropdownOptions,
  handleSave,
} from "./utils/saveNewPrimaryDiagnosisCode";
interface ClinicalDataTableProps {
  data: ClinicalData | null;
  onSave?: () => void;
  onClose: () => void;
}

export interface TableData {
  icd10_code: string | undefined;
  icd10_desc: string | undefined;
  recorded_date: string | undefined;
  valid_drugs: string | undefined;
}

const ClinicalDataTable = (props: ClinicalDataTableProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const primaryIcdCodes = usePrimaryIcdCodes();
  const [selectedPrimaryCode, setSelectedPrimaryCode] = useState<string>("");
  const [defaultPrimaryCode, setDefaultPrimaryCode] = useState("");

  useEffect(() => {
    if (primaryIcdCodes.length > 0) {
      const defaultCode = primaryIcdCodes[0];
      setDefaultPrimaryCode(defaultCode);
      if (!selectedPrimaryCode) {
        setSelectedPrimaryCode(defaultCode);
      }
    }
  }, [primaryIcdCodes, selectedPrimaryCode]);

  const { data: clinicalData, unsubscribe } = useSelector(
    (state: RootState) => state.medicalPaClinicalData,
  );
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const jcode_icd = useMemo(
    () => formatJcodeIcd(singleMedicalPaOrder || ({} as MedicalPaOrder)),
    [singleMedicalPaOrder],
  );
  const orderId = useSelector(
    (state: RootState) =>
      state.singleMedicalPaOrder.singleMedicalPaOrder?.order_id,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const dropdownOpts = useMemo(() => dropdownOptions(tableData), [tableData]);
  const getRowClassName = useCallback(
    (row: TableData) => {
      return row.icd10_code && primaryIcdCodes.includes(row.icd10_code)
        ? "bg-tertiaryGreen-50"
        : "";
    },
    [primaryIcdCodes],
  );
  const generateTableData = (clinicalData: ClinicalData) => {
    return Array.isArray(clinicalData?.entries)
      ? clinicalData.entries.map((entry) => ({
          icd10_code: entry.icd10_code,
          icd10_desc: entry.icd10_desc,
          recorded_date: entry.recorded_date,
          valid_drugs: entry.matched_jcodes
            ? entry.matched_jcodes.join(", ")
            : undefined,
        }))
      : [];
  };

  useEffect(() => {
    if (clinicalData) {
      const tableData = generateTableData(clinicalData) || [];
      setTableData(tableData);
    }
  }, [clinicalData]);
  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [unsubscribe]);
  const columns = [
    {
      label: "ICD Code",
      key: "icd10_code",
      order: 1,
      width: 10,
      type: TableCellType.STRING,
      sortable: false,
    },
    {
      label: "Description",
      key: "icd10_desc",
      order: 2,
      width: 50,
      type: TableCellType.STRING,
      sortable: false,
    },
    {
      label: "Recorded Date",
      key: "recorded_date",
      order: 3,
      width: 15,
      type: TableCellType.DATE,
      sortable: false,
    },
    {
      label: "Valid Drugs",
      key: "valid_drugs",
      order: 4,
      width: 25,
      type: TableCellType.STRING,
      sortable: false,
    },
  ];

  return (
    <Modal
      dialogId={ModalId.CLINICAL_DATA_TABLE}
      title="Dx Code"
      onSave={() => {
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          event_name: EventsName.PRIMARY_ICD_SAVED,
          org_id: orgId ?? "",
          order_id: orderId ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
            modal_name: ModalNamesForBigQuery.CLINICAL_DATA_TABLE,
            payer_name:
              singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
            old_primary_icd_code: defaultPrimaryCode,
            new_primary_icd_code: selectedPrimaryCode,
            jcode_icd,
            j_code: (
              getItemFromLocalStorage(
                `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
              ) ?? []
            ).join(","),
          },
        });
        handleSave(selectedPrimaryCode, tableData, orderId, {
          onStart: () => setIsSaving(true),
          onFinish: () => setIsSaving(false),
          onSave: props.onSave,
          onError: (err) => console.error(err),
        });
      }}
      saveButtonText={isSaving ? "Saving..." : "Save"}
      cancelText={"Cancel"}
      onClose={props.onClose}
      showSingleButton={false}
      heightPercentage={80}
      disableSave={isSaving}
      hideFooter={false}
    >
      <div className="flex flex-col items-start gap-6 self-stretch">
        <div className="primaryDx-select w-full">
          <Select
            id="primaryDx"
            label="Primary Dx Code"
            placeholder="Select the DX Code"
            options={dropdownOpts}
            defaultValue={defaultPrimaryCode}
            onOptionChange={(option) => {
              apiWorkerManager.enqueueAndProcess({
                patient_id: singleMedicalPaOrder?.patient_id ?? "",
                event_name: EventsName.DX_CODE_DROPDOWN_SELECTED,
                org_id: orgId ?? "",
                order_id: orderId ?? "",
                user_id: user?.id ?? "",
                user_email: user?.email ?? "",
                additional_data: {
                  screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                  modal_name: ModalNamesForBigQuery.CLINICAL_DATA_TABLE,
                  selected_dx_code: option.value,
                  assigned_primary_icd_code: primaryIcdCodes,
                  jcode_icd,
                  j_code: (
                    getItemFromLocalStorage(
                      `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
                    ) ?? []
                  ).join(","),
                  payer_name:
                    singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
                },
              });
              setSelectedPrimaryCode(option.value);
            }}
          />
        </div>

        <div className="flex flex-col gap-3 pb-4">
          <span className="text-h12 font-semibold leading-h12 text-primaryGray-1">
            Patient Dx Codes
          </span>

          <InfoBadge
            icon={<GreenBullet />}
            text="Preferred Primary Dx Code as per NCCN Guidelines"
            bgColor="bg-tertiaryGreen-50"
            borderColor="border-tertiaryGreen-700"
            borderWidth="border-very-thin"
            textColor="text-primaryGray-2"
            width="w-80"
            height="h-8"
          />

          <CustomTable
            tableName={TableNames.CLINICAL_DATA}
            tableHeaders={columns}
            tableData={tableData}
            hideSearchBar={true}
            count={tableData.length}
            pagesPerView={4}
            itemsPerPage={12}
            isFetching={false}
            rowClassName={getRowClassName}
          />
        </div>
      </div>
    </Modal>
  );
};
export default ClinicalDataTable;
